<template>
  <div class="survey-scale-question">
    <p class="survey-scale-question__title">{{ title }}</p>

    <div class="survey-scale-question__rows">
      <div
          v-for="(statement, index) in statements"
          :key="getStatementKey(statement, index)"
          class="survey-scale-question__row"
      >
        <div class="survey-scale-question__statement">{{ getStatementText(statement) }}</div>

        <div class="survey-scale-question__scale">
          <div class="survey-scale-question__options">
            <div
                v-for="value in scaleValues"
                :key="value"
                class="survey-scale-question__option"
            >
              <button
                  type="button"
                  class="survey-scale-question__node"
                  :class="{ 'survey-scale-question__node--selected': getValue(index) === value }"
                  :aria-label="`Оценка ${value}`"
                  @click="setValue(index, value)"
              >
                <span class="survey-scale-question__dot" />
              </button>
              <span class="survey-scale-question__label">{{ value }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'SurveyScaleQuestion',
  props: {
    title: {
      type: String,
      required: true,
    },
    statements: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    min: {
      type: Number,
      default: 1,
    },
    max: {
      type: Number,
      default: 9,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const scaleValues = computed(() => {
      const min = Number(props.min);
      const max = Number(props.max);

      const values = [];

      for (let value = min; value <= max; value += 1) {
        values.push(value);
      }

      return values;
    });

    const getValue = (index) => props.modelValue[index] ?? null;

    const setValue = (index, value) => {
      const next = [...props.modelValue];

      while (next.length < props.statements.length) {
        next.push(null);
      }

      next[index] = value;
      emit('update:modelValue', next);
    };

    const getStatementText = (statement) => {
      return typeof statement === 'object' && statement !== null ? statement.text : statement;
    };

    const getStatementKey = (statement, index) => {
      return typeof statement === 'object' && statement !== null ? statement.id : index;
    };

    return {
      scaleValues,
      getValue,
      setValue,
      getStatementText,
      getStatementKey,
    };
  },
};
</script>

<style scoped lang="scss">
$survey-answer-bg: #00000005;
$scale-dot-size: 14px;
$scale-dot-selected-size: 18px;
$scale-line-color: #d9d9d9;

.survey-scale-question {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    margin: 0 0 20px;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    border-radius: 8px;
    background: $survey-answer-bg;
  }

  &__statement {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
  }

  &__options {
    position: relative;
    display: flex;
    align-items: start;
    justify-content: space-between;
    width: 100%;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: calc(#{$scale-dot-selected-size} / 2);
      height: 1px;
      transform: translateY(-50%);
      background: $scale-line-color;
      pointer-events: none;
    }
  }

  &__option {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-rows: $scale-dot-selected-size auto;
    justify-items: center;
    gap: 6px;
    width: $scale-dot-selected-size;
    min-width: $scale-dot-selected-size;
  }

  &__node {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $scale-dot-selected-size;
    height: $scale-dot-selected-size;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  &__dot {
    position: relative;
    display: block;
    width: $scale-dot-size;
    height: $scale-dot-size;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    transition: width 0.15s ease, height 0.15s ease, box-shadow 0.15s ease;
  }

  &__node--selected &__dot {
    width: $scale-dot-selected-size;
    height: $scale-dot-selected-size;
    background: #fff;
    border: 1px solid var(--main-green);
    box-shadow: none;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 8px;
      height: 8px;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background: var(--main-green);
    }
  }

  &__label {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 110%;
    color: rgba(0, 0, 0, 0.88);
    text-align: center;
  }
}
</style>
